import React, { useEffect, useRef, useState } from 'react'
import { Nav, Navbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import icon from '../../../assets/sure_admin_icon.png'
import { IRootState } from '../../../redux/reducers'
import { NavItem, NavItemGroup } from '../types'

import { SignInButton } from './auth/SignInButton'
import SlideInMenu from './SlideInMenu'

type HeaderWithNavDropDownProps = {
  navItems: (NavItem | NavItemGroup)[]
  hasAppConfigurationError?: boolean
}

/**
 * HeaderWithNavDropDown component.
 *
 * This component renders a responsive header with a dropdown menu for navigation items.
 * It uses the react-bootstrap library for styling and layout. The dropdown menu toggles between
 * open and closed states based on user interaction.
 *
 * @param {HeaderWithNavDropDownProps} props - The props for the component.
 * @returns {JSX.Element} The rendered header with navigation dropdown component.
 */
const HeaderWithNavDropDown: React.FC<HeaderWithNavDropDownProps> = ({
  navItems,
  hasAppConfigurationError,
}): JSX.Element => {
  const { t } = useTranslation()
  const identity = useSelector((state: IRootState) => state.identity)
  const [fontSize, setFontSize] = useState('calc(5vw + 1vh)')
  const brandRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleResize = () => {
      if (brandRef.current) {
        const availableWidth = window.innerWidth * 0.7
        const vw = window.innerWidth * 0.05
        const vh = window.innerHeight * 0.01
        const calcFontSize = vw + vh
        const newFontSize = Math.min(availableWidth / 11, calcFontSize)
        setFontSize(`${newFontSize}px`)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <span style={{ overflowX: 'auto', width: '100%' }}>
      <Navbar
        variant="dark"
        style={{
          background: '#0096d6',
          width: '100%',
          minWidth: 'max-content',
        }}
      >
        <Nav
          className="mr-auto"
          defaultActiveKey="/keys"
          style={{ display: 'flex', alignItems: 'center', width: '95%' }}
        >
          <div style={{ flex: '0 0 10%' }}>
            <SlideInMenu
              navItems={navItems}
              hasAppConfigurationError={hasAppConfigurationError}
            />
          </div>
          <div
            style={{ flex: '0 0 90%', display: 'flex', alignItems: 'center' }}
            ref={brandRef}
          >
            <Navbar.Brand
              href="/keys"
              style={{ fontWeight: 500, fontSize: fontSize }}
              className="responsive-icon"
            >
              <img
                alt="KMS Logo"
                src={icon}
                className="d-inline-block align-top responsive-icon"
                style={{ margin: '10px', height: 'calc(7vw + 2.5vh)' }}
              />
              {t('kms_admin_portal')}
            </Navbar.Brand>
            <div
              style={{
                marginLeft: 'auto',
                textAlign: 'right',
                marginRight: '10px',
              }}
            >
              {!identity && !hasAppConfigurationError && <SignInButton />}
            </div>
          </div>
        </Nav>
      </Navbar>
    </span>
  )
}

export default HeaderWithNavDropDown
